import React from 'react';
import _ from 'underscore';

import * as MobileHelper from '~commonInfra/utils/MobileHelper';
import { ReactResizeDetector } from '~src/commonInfra/components/ReactResizeDetector';
import { HomeUserUtils } from '~src/home/utils/HomeUserUtils';
import { TOTAL_CARD_WIDTH } from '~src/spacesPlatform/components/card/CardViewStyles';
import {
  CreateContentButtonLocations
} from '~src/spacesPlatform/components/Explorer/CreateContent/CreateContentButton';
import { createContentButton } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';

import { Messages } from '../../codegen/Localize';
import { ConnectedSharedWithMeChannel } from '../../sharing/components/home/ConnectedSharedWithMeChannel';
import { ConnectedStartersChannel } from '../../starters/components/ConnectedStartersChannel';
import { SiteRoles } from '../../userManagement/users/serverApi/types/ISiteRole';
import { ChannelStyles } from '../styles/ChannelStyles';
import { HomeStyles } from '../styles/HomeStyles';
import { ConnectedFavoritesChannel } from './ConnectedFavoritesChannel';
import { ConnectedHomeRecommendationsChannel } from './ConnectedHomeRecommendationsChannel';
import { ConnectedRecentsChannel } from './ConnectedRecentsChannel';
import { ConnectedWelcomeChannel } from './ConnectedWelcomeChannel';
import { GuidedOnboardingWelcomeBanner } from './GuidedOnboardingWelcomeBanner';
import { LearnTableauChannel } from './LearnTableauChannel';
import { NoFavoritesAndRecentsChannel } from './NoFavoritesAndRecentsChannel';

export interface HomeStateProps {
  readonly userSiteRole: string;
  readonly favoritesCount: number;
  readonly recentsCount: number;
  readonly popularCount: number;
  readonly recommendationsCount: number;
  readonly sharedWithMeCount: number;
  readonly isFetchingFavorites: boolean;
  readonly isFetchingRecents: boolean;
  readonly isFetchingRecommendations: boolean;
  readonly isNamedSharingEnabled: boolean;
  readonly showWelcomeChannel: boolean;
  readonly recommendationsEnabled: boolean;
  readonly siteTypeName: string;
}

export interface HomeOwnProps {
  readonly siteName: string;
}

export interface HomeDispatchProps {
  readonly enterHome: () => void;
}

export interface HomeState {
  readonly maxCardsPerRow: number;
  editorContent: string;
}

export type HomeProps = HomeStateProps & HomeDispatchProps & HomeOwnProps;

export class Home extends React.PureComponent<HomeProps, HomeState> {

  private channelRef: React.RefObject<HTMLDivElement>;
  private debounceMaxCardsPerRow: () => void;

  constructor(props: HomeProps) {
    super(props);
    this.state = {
      maxCardsPerRow: 0,
      editorContent: ''
    };
    this.channelRef = React.createRef();
    this.debounceMaxCardsPerRow = _.debounce(() => this.updateMaxCardsPerRow(), 50);
  }

  public componentDidMount() {
    this.props.enterHome();
    this.updateMaxCardsPerRow();
  }

  public componentDidUpdate(prevProps: HomeProps) {
    if (!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.props.enterHome();
    }
  }

  /**
   * Calculates how many cards fit into the width of the channel. Used to help limit channels to N amount of rows.
   */
  private updateMaxCardsPerRow() {
    const channelWidth = this.channelRef?.current?.offsetWidth + ChannelStyles.horizontalMarginBetweenCards;  // The channel has a negative margin and can fit an additional margin between cards
    const cardWidth = TOTAL_CARD_WIDTH + ChannelStyles.horizontalMarginBetweenCards + ChannelStyles.cardBorder;
    const maxCardsPerRow = Math.floor(channelWidth / cardWidth);
    this.setState({ maxCardsPerRow });
  }

  private shouldRenderStartersChannel() {
    return (
      (this.props.userSiteRole === SiteRoles.ServerAdministrator ||
        this.props.userSiteRole === SiteRoles.SiteAdministratorCreator ||
        this.props.userSiteRole === SiteRoles.Creator ||
        this.props.userSiteRole === SiteRoles.ExplorerCanPublish)
        && !MobileHelper.isPhoneSizedScreen());
  }

  private shouldRenderSharedWithMeChannel() {
    return (
      this.props.sharedWithMeCount > 0 && this.props.isNamedSharingEnabled
    );
  }

  private shouldShowWelcomeChannel() {
    return this.props.showWelcomeChannel && !HomeUserUtils.isGuidedOnboardingEnabled(this.props.siteTypeName);
  }

  private shouldShowGuidedOnboardingWelcomeBanner() {
    return HomeUserUtils.isGuidedOnboardingEnabled(this.props.siteTypeName);
  }

  public render() {
    const noFavsAndRecents = () => {
      return !this.props.isFetchingFavorites &&
        !this.props.isFetchingRecents &&
        this.props.favoritesCount === 0 &&
        this.props.recentsCount === 0;
    };

    const createButton =
      <div className={HomeStyles.createButton}>
        {createContentButton({ location: CreateContentButtonLocations.Home, spaceType: 'home'})}
      </div>;

    return (
      <React.Fragment>
        {this.shouldShowGuidedOnboardingWelcomeBanner() && (
          <GuidedOnboardingWelcomeBanner />
        )}
        <section className={HomeStyles.root} ref={this.channelRef}>
          <header className={HomeStyles.pageHeader}>
            <h1>{Messages.home_title()}</h1>
            {!this.shouldShowWelcomeChannel() && this.props.userSiteRole !== SiteRoles.Viewer && createButton}
          </header>
          {this.shouldShowWelcomeChannel() && <ConnectedWelcomeChannel />}
          {this.props.favoritesCount > 0 && <ConnectedFavoritesChannel maxCardsPerRow={this.state.maxCardsPerRow}></ConnectedFavoritesChannel>}
          {this.props.recentsCount > 0 && <ConnectedRecentsChannel maxCardsPerRow={this.state.maxCardsPerRow}></ConnectedRecentsChannel>}
          {noFavsAndRecents() && <NoFavoritesAndRecentsChannel />}
          {this.shouldRenderSharedWithMeChannel() &&
            <ConnectedSharedWithMeChannel maxCardsPerRow={this.state.maxCardsPerRow}></ConnectedSharedWithMeChannel>
          }
          {this.props.recommendationsCount > 0 &&
            this.props.recommendationsEnabled &&
            <ConnectedHomeRecommendationsChannel
              maxCardsPerRow={this.state.maxCardsPerRow}
            />
          }
          {this.shouldRenderStartersChannel() &&
            <ConnectedStartersChannel maxCardsPerRow={this.state.maxCardsPerRow}></ConnectedStartersChannel>}
          <LearnTableauChannel userSiteRole={this.props.userSiteRole}></LearnTableauChannel>
          <ReactResizeDetector targetRef={this.channelRef} handleWidth handleHeight skipOnMount={true} onResize={this.debounceMaxCardsPerRow} />
        </section>
      </React.Fragment>
    );
  }
}
